import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Divider
} from '@mui/material';
import { EmojiEvents, Groups } from '@mui/icons-material';
import {
  fetchLeaderboard,
  fetchPercentile,
  Leaderboard,
  Percentile
} from '../../services/rankingService';

/**
 * Where this score placed (monetization feature #02).
 *
 * The interesting case is the small cohort. Percentile over a handful of attempts is
 * noise dressed as a statistic, so the service returns null rather than a number, and
 * this renders "not enough attempts yet" instead of inventing a rank. Showing "top 50%"
 * to the second student to sit a paper would be worse than showing nothing.
 */

interface Props {
  packageId: string;
  score: number;
}

/** Below this the cohort is too thin for a placing to mean anything to a reader. */
const MIN_INTERESTING_COHORT = 5;

const ordinal = (n: number): string => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const RankPanel: React.FC<Props> = ({ packageId, score }) => {
  const [percentile, setPercentile] = useState<Percentile | null>(null);
  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBoard, setShowBoard] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!packageId) return undefined;

    setLoading(true);
    Promise.all([fetchPercentile(packageId, score), fetchLeaderboard(packageId, 10)]).then(
      ([p, b]) => {
        if (cancelled) return;
        setPercentile(p);
        setBoard(b);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [packageId, score]);

  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      </Paper>
    );
  }

  // Nothing to say and no reason to occupy the screen saying it.
  if (!percentile && !board) return null;

  const cohortSize = percentile?.cohortSize ?? board?.cohortSize ?? 0;
  const thinCohort = cohortSize < MIN_INTERESTING_COHORT || percentile?.percentile == null;

  return (
    <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <EmojiEvents color="primary" />
        <Typography variant="h5">How you placed</Typography>
      </Box>

      {thinCohort ? (
        <Typography variant="body2" color="text.secondary">
          {cohortSize <= 1
            ? 'You are the first to sit this paper. A ranking will appear once more students have taken it.'
            : `Only ${cohortSize} students have sat this paper so far — too few for a meaningful ranking. Check back once more have taken it.`}
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              mb: 1
            }}
          >
            <Box>
              <Typography variant="h3" color="primary" sx={{ lineHeight: 1.1 }}>
                {percentile?.percentile?.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {percentile?.callout ??
                  `You scored higher than ${percentile?.percentile?.toFixed(
                    1
                  )}% of students who sat this paper.`}
              </Typography>
            </Box>

            {percentile?.rank != null && (
              <Chip
                icon={<EmojiEvents />}
                color="primary"
                variant="outlined"
                label={`Ranked ${ordinal(percentile.rank)} of ${cohortSize}`}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Measured against {cohortSize} completed attempts at this paper.
          </Typography>
        </>
      )}

      {board && board.entries.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button
            size="small"
            startIcon={<Groups />}
            onClick={() => setShowBoard((v) => !v)}
            sx={{ mb: showBoard ? 1 : 0 }}
          >
            {showBoard ? 'Hide leaderboard' : `Show top ${board.entries.length}`}
          </Button>

          {showBoard && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell align="right">Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {board.entries.map((e) => (
                  <TableRow key={`${e.rank}-${e.studentName}`}>
                    <TableCell>{e.rank}</TableCell>
                    <TableCell>{e.studentName}</TableCell>
                    <TableCell align="right">
                      {e.percentage != null
                        ? `${e.percentage.toFixed(1)}%`
                        : `${e.score ?? '—'}/${e.totalScore ?? '—'}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Paper>
  );
};

export default RankPanel;
